import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import SiteHeaderV2 from "@/components/v2/SiteHeaderV2";
import { Button } from "@/components/ui/button";
import { getMission, TOTAL_MISSIONS, MISSION_SECTIONS } from "@/data/guideV2";
import { useGuideV2Progress } from "@/hooks/useGuideV2Progress";
import { useAuth } from "@/context/AuthContext";
import PaperLogsEducation from "@/components/paperlog/PaperLogsEducation";
import LlcExplainer from "@/components/LlcExplainer";
import EinExplainer from "@/components/EinExplainer";
import AuthorityExplainer from "@/components/AuthorityExplainer";
import Boc3InsuranceExplainer from "@/components/Boc3InsuranceExplainer";
import UcrClearinghouseExplainer from "@/components/UcrClearinghouseExplainer";
import EldIftaExplainer from "@/components/EldIftaExplainer";
import DqFileExplainer from "@/components/DqFileExplainer";
import DrugConsortiumExplainer from "@/components/DrugConsortiumExplainer";
import SafetyAuditExplainer from "@/components/SafetyAuditExplainer";
import FactoringExplainer from "@/components/FactoringExplainer";
import LaneOptimizationExplainer from "@/components/LaneOptimizationExplainer";
import DispatchScalingExplainer from "@/components/DispatchScalingExplainer";

// Per-mission plain-English explainers (mirrors the V1 guide).
const MISSION_EXPLAINERS = {
  1: LlcExplainer, 2: EinExplainer, 3: AuthorityExplainer, 4: Boc3InsuranceExplainer,
  5: UcrClearinghouseExplainer, 6: EldIftaExplainer, 7: DqFileExplainer, 8: DrugConsortiumExplainer,
  9: SafetyAuditExplainer, 10: FactoringExplainer, 11: LaneOptimizationExplainer, 12: DispatchScalingExplainer,
};
import {
  ArrowLeft, ArrowRight, CheckCircle2, Circle, Clock, ExternalLink, Info, HelpCircle,
  ListChecks, Flag, FolderOpen, Trophy, Compass, LayoutGrid, Save, Quote, Download,
  FileSpreadsheet, Calculator, FileText, BookMarked,
} from "lucide-react";

// --- Section shell -----------------------------------------------------------
function Section({ id, n, label, icon: Icon, children }) {
  return (
    <section id={id} data-testid={`mission-section-${id}`} className="scroll-mt-32">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-8 h-8 rounded-lg bg-slate-900 text-white font-mono text-sm font-bold flex items-center justify-center shrink-0">
          {n}
        </span>
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-brand" />
          <h2 className="font-heading text-lg font-black tracking-tight text-slate-900">{label}</h2>
        </div>
      </div>
      <div className="pl-11">{children}</div>
    </section>
  );
}

// --- Worksheet field ---------------------------------------------------------
function Field({ n, field, getField, setField, onSaved }) {
  const value = getField(n, field.id);
  const base = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition-shadow";
  const change = (v) => { setField(n, field.id, v); onSaved(); };
  const filled = String(value || "").trim() !== "";
  return (
    <div data-testid={`worksheet-field-${field.id}`}>
      <label className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-2">
        {filled ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Circle className="w-4 h-4 text-slate-300" />}
        {field.label}
      </label>
      {field.type === "textarea" ? (
        <textarea rows={3} className={base} placeholder={field.placeholder || ""} value={value} onChange={(e) => change(e.target.value)} />
      ) : field.type === "select" ? (
        <select className={base} value={value} onChange={(e) => change(e.target.value)}>
          <option value="">Select…</option>
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : field.type === "money" ? (
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
          <input inputMode="decimal" className={`${base} pl-7`} placeholder={field.placeholder || ""} value={value} onChange={(e) => change(e.target.value)} />
        </div>
      ) : (
        <input type={field.type === "date" ? "date" : "text"} className={base} placeholder={field.placeholder || ""} value={value} onChange={(e) => change(e.target.value)} />
      )}
    </div>
  );
}

// --- Resource link card ------------------------------------------------------
function ResourceLink({ item, icon: Icon, testid }) {
  return (
    <a
      href={item.url} target="_blank" rel="noopener noreferrer" data-testid={testid}
      className="group flex items-start gap-3 rounded-xl border border-slate-200 p-4 hover:border-brand transition-colors bg-white"
    >
      <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 group-hover:bg-brand/10 transition-colors">
        <Icon className="w-4 h-4 text-slate-500 group-hover:text-brand transition-colors" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <span className="font-semibold text-slate-900 text-sm leading-snug">{item.label}</span>
          <ExternalLink className="w-4 h-4 text-slate-300 group-hover:text-brand shrink-0 transition-colors" />
        </div>
        {item.desc && <p className="text-xs text-slate-500 mt-1 leading-relaxed">{item.desc}</p>}
      </div>
    </a>
  );
}

const sec = (id) => MISSION_SECTIONS.find((x) => x.id === id);

export default function ModuleV2() {
  const { n } = useParams();
  const navigate = useNavigate();
  const { isDone, toggleModule, getField, setField, isActionDone, toggleAction, actionProgress, count } = useGuideV2Progress();
  const { user } = useAuth();
  const m = getMission(n);
  const MissionExplainer = m ? MISSION_EXPLAINERS[m.n] : null;
  const [savedAt, setSavedAt] = useState(0);

  useEffect(() => { window.scrollTo(0, 0); }, [n]);
  useEffect(() => {
    if (!savedAt) return;
    const t = setTimeout(() => setSavedAt(0), 2000);
    return () => clearTimeout(t);
  }, [savedAt]);

  if (!m) {
    return (
      <div className="min-h-screen bg-white">
        <SiteHeaderV2 />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-heading text-3xl font-black text-slate-900">Mission not found</h1>
          <Link to="/v2" className="text-brand font-bold mt-4 inline-block">Back to your dashboard</Link>
        </div>
      </div>
    );
  }

  const done = isDone(m.n);
  const prev = getMission(m.n - 1);
  const next = getMission(m.n + 1);
  const ap = actionProgress(m.n, m.checklist.length);
  const guidePct = Math.round((count / TOTAL_MISSIONS) * 100);
  const r = m.resources;

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteHeaderV2 />

      {/* Sub-bar */}
      <div className="sticky top-16 z-20 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 h-12 flex items-center justify-between">
          <Link to="/v2" data-testid="v2-back-dashboard" className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-500 hover:text-slate-900 transition-colors">
            <LayoutGrid className="w-4 h-4" /> Dashboard
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/v2/glossary" data-testid="mission-glossary-link" className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-500 hover:text-brand transition-colors">
              <BookMarked className="w-4 h-4" /> <span className="hidden sm:inline">Glossary</span>
            </Link>
            <span className="text-xs font-bold text-slate-400 tabular">Mission {m.n} of {TOTAL_MISSIONS}</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="font-mono text-4xl font-black tabular text-brand">{String(m.n).padStart(2, "0")}</span>
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-300 border border-white/15 rounded-full px-3 py-1 inline-flex items-center gap-1.5">
                <Compass className="w-3.5 h-3.5" /> {m.phase}
              </span>
              <span className="text-xs tracking-[0.1em] uppercase font-bold text-slate-400 inline-flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> {m.duration}
              </span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black leading-[1.02]">{m.title}</h1>
            <p className="mt-4 text-slate-300 leading-relaxed">{m.tagline}</p>
          </motion.div>
        </div>
      </section>

      {/* Sections */}
      <div className="max-w-3xl mx-auto px-6 py-12 space-y-12">
        {MissionExplainer && <MissionExplainer />}
        {m.n === 6 && (
          <PaperLogsEducation practiceTo="/v2/pro/paper-logs" isPaid={!!user?.subscription_active} />
        )}
        {/* 1 · What It Is */}
        <Section {...sec("what-it-is")} icon={Info}>
          <p className="text-slate-700 leading-relaxed">{m.whatItIs}</p>
        </Section>

        {/* 2 · Why It Matters */}
        <Section {...sec("why-it-matters")} icon={HelpCircle}>
          <p className="text-slate-700 leading-relaxed">{m.whyItMatters}</p>
          {m.mentorTip && (
            <div className="mt-4 rounded-2xl bg-brand/5 border border-brand/20 p-5" data-testid="v2-mentor-note">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-full bg-brand flex items-center justify-center shrink-0">
                  <Quote className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">Mentor tip</span>
              </div>
              <p className="text-slate-800 text-sm leading-relaxed">{m.mentorTip}</p>
            </div>
          )}
        </Section>

        {/* 3 · How To Do It */}
        <Section {...sec("how-to-do-it")} icon={ListChecks}>
          {m.howToIntro && <p className="text-slate-700 leading-relaxed mb-4">{m.howToIntro}</p>}
          <ul className="space-y-3">
            {m.howToSteps.map((t, i) => (
              <li key={i} className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 font-mono text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                <span className="text-slate-700 leading-relaxed text-sm sm:text-base">{t}</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* 4 · What To Do Next */}
        <Section {...sec("what-to-do-next")} icon={Flag}>
          <div className="rounded-2xl bg-slate-900 text-white p-6" data-testid="v2-next-action">
            <div className="text-xs uppercase tracking-[0.15em] font-bold text-brand mb-2">Your one next move</div>
            <p className="text-lg font-semibold leading-snug">{m.whatToDoNext}</p>
            <p className="text-sm text-slate-400 mt-3">Do this one thing, then come back and check it off below.</p>
          </div>
        </Section>

        {/* 5 · RouteHaul Resources */}
        <Section {...sec("routehaul-resources")} icon={FolderOpen}>
          <p className="text-sm text-slate-500 -mt-1 mb-5">Everything for this mission in one place — worksheet, tools and official links.</p>

          {/* Worksheet */}
          <div className="rounded-2xl bg-white border border-slate-200 p-6 mb-5" data-testid="v2-worksheet">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-brand" />
                <span className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Worksheet</span>
              </div>
              <span className={`inline-flex items-center gap-1 text-xs font-bold transition-opacity ${savedAt ? "opacity-100 text-emerald-600" : "opacity-0"}`} data-testid="worksheet-saved">
                <Save className="w-3.5 h-3.5" /> Saved
              </span>
            </div>
            <p className="text-sm text-slate-500 mb-5">{r.worksheet.intro} <span className="text-slate-400">Saves automatically on this device.</span></p>
            <div className="space-y-5">
              {r.worksheet.fields.map((f) => (
                <Field key={f.id} n={m.n} field={f} getField={getField} setField={setField} onSaved={() => setSavedAt(Date.now())} />
              ))}
            </div>
          </div>

          {/* Templates */}
          {r.templates.length > 0 && (
            <div className="mb-5">
              <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-3">Templates</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {r.templates.map((t, i) => <ResourceLink key={t.url} item={t} icon={FileText} testid={`v2-template-${m.n}-${i}`} />)}
              </div>
            </div>
          )}

          {/* Calculators */}
          {r.calculators.length > 0 && (
            <div className="mb-5">
              <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-3">Calculators</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {r.calculators.map((c, i) => <ResourceLink key={c.url} item={c} icon={Calculator} testid={`v2-calculator-${m.n}-${i}`} />)}
              </div>
            </div>
          )}

          {/* Downloads */}
          {r.downloads.length > 0 && (
            <div className="mb-5">
              <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-3">Downloads</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {r.downloads.map((d, i) => <ResourceLink key={d.url} item={d} icon={Download} testid={`v2-download-${m.n}-${i}`} />)}
              </div>
            </div>
          )}

          {/* Official resources */}
          {r.official.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-3">Official resources</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {r.official.map((o, i) => <ResourceLink key={o.url} item={o} icon={ExternalLink} testid={`v2-resource-${m.n}-${i}`} />)}
              </div>
            </div>
          )}

          {r.templates.length === 0 && r.calculators.length === 0 && (
            <p className="text-xs text-slate-400 mt-4">Mission-specific templates and calculators will appear here as we build this mission out.</p>
          )}
        </Section>

        {/* 6 · Mission Complete */}
        <Section {...sec("mission-complete")} icon={CheckCircle2}>
          <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="v2-action-plan">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Completion checklist</span>
              <span className="text-xs font-bold text-slate-500 tabular">{ap.done}/{ap.total}</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-4">
              <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${ap.pct}%` }} />
            </div>
            <ul className="space-y-1">
              {m.checklist.map((t, i) => {
                const checked = isActionDone(m.n, i);
                return (
                  <li key={i}>
                    <button
                      onClick={() => toggleAction(m.n, i)}
                      data-testid={`v2-action-${m.n}-${i}`}
                      className="w-full flex items-start gap-3 text-left rounded-xl p-2.5 hover:bg-slate-50 transition-colors"
                    >
                      {checked
                        ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                        : <Circle className="w-5 h-5 text-slate-300 shrink-0 mt-0.5" />}
                      <span className={`text-sm leading-relaxed ${checked ? "text-slate-400 line-through" : "text-slate-800"}`}>{t}</span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Progress update */}
            <div className="mt-5 pt-5 border-t border-slate-100">
              <div className="flex items-center justify-between text-xs font-bold mb-2">
                <span className="text-slate-500 uppercase tracking-[0.15em]">Guide progress</span>
                <span className="text-slate-900 tabular">{count}/{TOTAL_MISSIONS} · {guidePct}% to authority</span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${guidePct}%` }} />
              </div>
            </div>

            <button
              onClick={() => toggleModule(m.n)}
              data-testid="v2-mark-complete"
              className={`mt-5 w-full inline-flex items-center justify-center gap-2 h-12 rounded-full text-sm font-bold transition-colors ${
                done ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-slate-900 text-white hover:bg-slate-800"
              }`}
            >
              {done ? <><CheckCircle2 className="w-5 h-5" /> Mission completed</> : <><Circle className="w-5 h-5" /> Mark mission complete</>}
            </button>
          </div>

          {done && (
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
              className="mt-4 rounded-2xl bg-emerald-600 text-white p-5 flex items-center gap-4" data-testid="v2-milestone"
            >
              <Trophy className="w-7 h-7 shrink-0" strokeWidth={2.2} />
              <div>
                <div className="text-xs uppercase tracking-[0.15em] font-bold text-emerald-100">Milestone reached</div>
                <p className="font-heading font-bold leading-tight">{m.milestone}</p>
              </div>
            </motion.div>
          )}
        </Section>
      </div>

      {/* Prev / next */}
      <section className="border-t border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-8 flex items-center justify-between gap-4">
          {prev ? (
            <Link to={`/v2/module/${prev.n}`} data-testid="v2-prev-module" className="group flex items-center gap-3 text-left min-w-0">
              <ArrowLeft className="w-5 h-5 text-slate-400 group-hover:text-brand transition-colors shrink-0" />
              <span className="min-w-0">
                <span className="block text-xs text-slate-400">Previous</span>
                <span className="block font-semibold text-slate-900 text-sm truncate">{prev.title}</span>
              </span>
            </Link>
          ) : <span />}
          {next ? (
            <Link to={`/v2/module/${next.n}`} data-testid="v2-next-module" className="group flex items-center gap-3 text-right min-w-0">
              <span className="min-w-0">
                <span className="block text-xs text-slate-400">Next</span>
                <span className="block font-semibold text-slate-900 text-sm truncate">{next.title}</span>
              </span>
              <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-brand transition-colors shrink-0" />
            </Link>
          ) : (
            <Button onClick={() => navigate("/v2")} data-testid="v2-finish" className="rounded-full bg-brand hover:bg-brand-hover text-white font-bold h-12 px-6">
              Back to dashboard <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </section>
    </div>
  );
}
